import { Component, Injector, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { Resource } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { RestrictionService } from 'src/app/controls/restrictions/services/restriction.service';
import { WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { YesNoDialogComponent } from '../../yes-no-dialog/yes-no-dialog.component';
import { ClBaseComponent } from '../cl-base/cl-base.component';
import { Document } from 'src/app/services/documents/documents';
import { text } from 'linkifyjs';
import { DocumentChip } from '../../checklist-chips/document-chips.component';
import { ProcessRestrictionResult, RadSurveyRequired } from '../../restrictions/services/restriction';
import { SignalRService } from 'src/app/services/signal-r/signal-r.service';
import { RestrictionImpactSignalRResponse } from 'src/app/services/signal-r/general-signal-r-response';
import { WFSaveOptions } from 'src/app/common/enumerations/enumerations';

@Component({
  selector: 'cl-restrictions',
  templateUrl: './cl-restrictions.component.html',
  styleUrls: ['./cl-restrictions.component.scss']
})
export class ClRestrictionsComponent extends ClBaseComponent implements OnInit, OnChanges, OnDestroy {

  documents!: string;

  resource?: Resource;
  resources!: Resource[];
  resources$!: Observable<Resource[]>;
  resourcesSubs!: Subscription;

  task?: WfTaskLocalResource;
  manual!: boolean;
  override suggested = false;

  validateRS?: boolean;
  restrictionImpactSubs?: Subscription;

  constructor(
    protected override injector: Injector,
    private service: RestrictionService,
    private signalRService: SignalRService,
  ) {
    super(injector);
  }

  override ngOnDestroy(): void {
    this.resourcesSubs?.unsubscribe();
    this.restrictionImpactSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['valueString'] || changes['configuration'] || changes['disabled']) {
      this.loadResources();
      this.loadData();
    }
  }

  /**
   * Listens for effective SCF/RSF changes and refreshes only this component when its location is affected.
   */
  ngOnInit() {
    this.restrictionImpactSubs = this.signalRService.restrictionImpact$.subscribe(impact => {
      this.refreshFromRestrictionImpact(impact);
    });
  }

  get textToDisplay(): string {
    if (!this.documents) {
      return 'No active forms found for the';
    }
    return 'Active forms found for the';
  }

  /**
   * Explains why an unchecked Startup Checklist restrictions task is recommended for manual review.
   * @returns The persisted recommendation reason, or a resource-specific no-active-forms message.
   */
  get suggestionTooltip(): string {
    const genericReason = 'No active restrictions or Rad Survey requirements were found.';
    if (!this.documents && (!this.suggestionReason || this.suggestionReason === genericReason)) {
      return `No active forms were found for ${this.resource?.name?.trim() ?? 'this resource'}. Review the result and sign this task manually.`;
    }

    return this.suggestionReason || 'ASCC recommends reviewing and signing this task manually.';
  }

  /**
   * Loads resources from the store and resolves the configured location used by the message label.
   */
  loadResources() {
    this.resources$ = this.store.select(state => state.Resources.data);
    this.resourcesSubs = this.resources$.subscribe(data => {
      if (data.length) {
        this.resources = data;
        const locationID = this.getConfiguredLocationID();
        this.resource = this.resources.find(x => x.id == locationID);
      }
    });
  }

  loadData() {
    const config = this.utils.JSONparse(this.configuration);
    const { val: locationID } = config;
    this.validateRS = config?.validateRS;
    this.resource = this.resources?.find(x => x.id == locationID);
    const wfSectionLocal = this.checklist?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.wfStepLocals?.some(t => t.wfTaskLocals.some(x => x.id === this.taskID)));
    const wfStepLocal = wfSectionLocal?.wfStepLocals?.find(s => s.wfTaskLocals.filter(x => x.id == this.taskID).length > 0);
    this.task = wfStepLocal?.wfTaskLocals?.find(x => x.id == this.taskID);
    const val = this.utils.JSONparse(this.valueString);
    this.checked = val?.user != null;
    this.suggested = val?.suggested === true;
    this.manual = val?.manualCheck;
    this.documents = val?.documents;
    if (val?.user) {
      this.user = val.user === -1
        ? { id: val.user, name: 'ASCC', initials: 'ASCC' }
        : this.getUsers()?.find(u => u.id === val.user);
      if (!val.manualCheck)
        this.disabled = val.user === -1;
    }
    if (this.checked && !wfStepLocal?.wfStepStatusLocals?.filter(x => x.enabled || !x.hidden).map(x => x.status).includes(this.checklist?.status)) {
      this.displayInfo();
    }
    else {
      if (locationID && !val?.manualCheck) {
        this.getRestrictions();
      }
    }
  }

  ppr?: ProcessRestrictionResult;
  radSurveyRequired = RadSurveyRequired;

  /**
   * Re-evaluates the configured location against the current active restrictions.
   * @param persist Whether automatic checkbox changes should be emitted to the checklist.
   */
  async getRestrictions(persist = true): Promise<void> {
    const locationID = this.getConfiguredLocationID();

    if (!locationID) {
      this.displayInfo('');
      return;
    }

    const ppr = await firstValueFrom(
      this.service.CheckByLocationID(locationID)
    );

    if (!ppr) {
      this.ppr = undefined;
      this.displayInfo('');
      if (!persist) {
        this.checked = true;
        this.disabled = true;
      }
      return;
    }

    this.ppr = ppr;

    const affectedForms = ppr.affectedForms ?? [];
    const radSurveyAffectedForms = ppr.radSurveyAffectedForms ?? [];

    const formsToDisplay = this.validateRS
      ? [...affectedForms, ...radSurveyAffectedForms]
      : affectedForms;

    this.displayInfo(
      formsToDisplay.length
        ? [...new Set(formsToDisplay.map(x => x.serialNo))].join(', ')
        : ''
    );

    if (this.editor || this.builder) {
      return;
    }

    const hasActiveRestrictions = affectedForms.length > 0;
    const hasRadSurveyRequirement = !!this.validateRS && radSurveyAffectedForms.length > 0;
    const isReady = !hasActiveRestrictions && !hasRadSurveyRequirement;
    const wasChecked = !!this.checked;

    // An automatic restriction result recommends manual review; it must not complete the task.
    this.checked = false;
    this.user = isReady ? null : this.user;
    this.suggested = isReady;
    this.disabled = hasActiveRestrictions;

    if (!persist) {
      return;
    }

    if (isReady) {
      this.setValues(null, true, false);
    }
    else if (hasActiveRestrictions && wasChecked) {
      this.setValues(null, false, true);
    }
  }

  /**
   * Refreshes this restriction component after a form activation or amendment becomes effective.
   * @param impact The accelerator IDs affected by the effective form change.
   */
  private refreshFromRestrictionImpact(impact: RestrictionImpactSignalRResponse): void {
    const locationID = this.getConfiguredLocationID();
    if (!locationID || this.manual || this.editor || this.builder) {
      return;
    }

    if (!impact.refreshAll && !impact.acceleratorIDs?.includes(locationID)) {
      return;
    }

    void this.getRestrictions(false);
  }

  /**
   * Reads and validates the location identifier configured for this component.
   * @returns The configured positive resource ID, or null when the configuration is incomplete.
   */
  private getConfiguredLocationID(): number | null {
    const { val } = this.utils.JSONparse(this.configuration) ?? {};
    const locationID = Number(val);
    return Number.isInteger(locationID) && locationID > 0 ? locationID : null;
  }


  reset() {
    this.manual = false;
    this.checkboxChanged({ checked: false });
  }

  displayInfo(serialNumbers: string | null | undefined = null) {
    this.warning = !!serialNumbers;
    this.moreInfo = serialNumbers;
    this.documents = serialNumbers ?? '';
  }

  checkboxChanged(e: any) {
    this.checked = e.checked;
    if (this.checked && this.warning) {
      this.dialog.open(YesNoDialogComponent, {
        width: '500px',
        data: {
          message: this.getMessage('RestrictionsDocuments_Warning').description.replace('{location}', this.resource?.name ?? '').replace('{documents}', this.moreInfo ?? ''),
          icon: 'stop',
          labelButtonCancel: 'Cancel',
          labelButtonAccept: 'Continue',
          changeButtonLabels: true
        },
        }).afterClosed().subscribe(data => {
        if (data) {
          this.setValues(this.getCurrentUser()?.id, false, true);
        }
        else {
          this.checked = false;
          this.loadData();
        }
      });
    }
    else if (this.checked) {
      this.setValues(this.getCurrentUser()?.id, false, true);
    }
    else {
      const canRecommend = !this.warning;
      this.suggested = canRecommend;
      this.setValues(null, canRecommend, true);
    }
  }

  /**
   * Persists the restriction result and distinguishes automatic recommendations from manual signatures.
   * @param userID User who manually checked the task, or null for an automatic refresh.
   * @param suggested Whether the automatic result recommends a manual signature.
   * @param sign Whether the save should run the manual approval flow.
   */
  setValues(userID?: number | null, suggested = false, sign = false) {
    const valueString = this.utils.JSONstringify({
      documents: this.documents,
      user: userID,
      manualCheck: (userID && userID != -1),
      suggested,
      suggestionReason: suggested ? this.suggestionTooltip : null
    });
    if (this.valueString != valueString) {
      const saveEvent: any = { text: valueString };
      if (sign)
        saveEvent.sign = { checked: this.checked };
      else
        saveEvent.option = WFSaveOptions.AUTO;
      this.save.emit(saveEvent);
    }
  }

}
